




































































































































































































































































import { Values } from 'vue-i18n';
import { Component, Model, Prop } from 'vue-property-decorator';
import { staffTabAccess } from '../common';
import { CollapsiblePane, ComponentBase, Overlay, PaginationPages } from '../components';
import { Comp, Dict, Version } from '../types';
import { IPositionVariantItem } from './types';


// region Local types
interface IPositionBase {
    type: string;
    children: Array<IPositionItem>;
}

interface IPosition extends IPositionBase {
    type: 'position';
    data: Dict.Position;
}

interface IPositionVariants extends IPositionBase {
    type: 'variant';
    data: Dict.PositionVariants;
}

type IPositionItem = IPosition | IPositionVariants;

type IPositionItemSorter = (item1: IPositionItem, item2: IPositionItem) => number;

/**
 * Строка таблицы
 */
interface IRow {
    /**
     * Должность, загруженная из сервера
     */
    data: IPositionItem;

    /**
     * Признак "строка выбрана"
     */
    selected: boolean;

    _showDetails: boolean;
}
// endregion


// region Utils
const modelChangeEvent = 'change';
const itemsPerPageVariants: Array<Comp.DropdownItemDef<number>> = [
    { text: '10', value: 10 },
    { text: '25', value: 25 },
    { text: '50', value: 50 },
    { text: '100', value: 100 }
];
const hasDifference = (positions1: Array<IPositionVariantItem>, positions2: Array<IPositionItem>): boolean => {
    if (positions1.length !== positions2.length) {
        return true;
    }

    const position1Ids = positions1.map(position => getPositionVariantItemId(position)).sort();
    const position2Ids = positions2.map(position => getPositionItemId(position)).sort();

    for (let i = 0; i < position1Ids.length; i++) {
        if (position1Ids[i] !== position2Ids[i]) {
            return true;
        }
    }

    return false;
};

const defaultItemSorter: IPositionItemSorter = (item1, item2) => {
    const getId = (item: IPositionItem): number => {
        if (item.type === 'position') {
            return item.data.id!!;
        } else {
            return item.data.main!!.id!!;
        }
    };

    const id1 = getId(item1);
    const id2 = getId(item2);

    return (id1 - id2);
};

const getPositionVariantItemId = (item: IPositionVariantItem): string => {
    const variant = item.variant;
    if (variant === null) {
        return `position--${item.position.id}`;
    } else {
        return `variant--${variant.id}`;
    }
};

const getPositionItemId = (item: IPositionItem): string => {
    if (item.type === 'position') {
        return `position--${item.data.id}`;
    } else {
        return `variant--${item.data.id}`;
    }
};
// endregion


@Component({
    components: {
        CollapsiblePane,
        Overlay,
        Pagination: PaginationPages
    }
})
export default class PositionSelection extends ComponentBase {
    constructor() {
        super('');
    }


    // region Модель и свойства
    @Model(modelChangeEvent, {
        required: false,
        default: () => []
    })
    public readonly value!: Array<IPositionVariantItem>;

    @Prop({
        type: Boolean,
        required: false,
        default: false
    })
    public readonly multiple!: boolean;

    @Prop({
        type: Boolean,
        required: false,
        default: false
    })
    public readonly selectOnRowClick!: boolean;

    @Prop({
        type: Array,
        required: false,
        default: () => []
    })
    public readonly allowedPosLevels!: number[];

    @Prop({
        type: Object,
        required: false,
        default: () => null,
    })
    public readonly version!: Version | null;
    // endregion


    // region Lifecycle
    // noinspection JSUnusedLocalSymbols
    protected created() {
        this.$watch('value', (value: Array<IPositionVariantItem>) => {
            if (hasDifference(value, this.selectedItems)) {
                this.copySelectedItemsFromValue();
            }
        });
        this.copySelectedItemsFromValue();

        this.$watch('selectedItems', (selectedItems: Array<IPositionItem>) => {
            if (hasDifference(this.value, selectedItems)) {
                this.$emit(modelChangeEvent, selectedItems.map((selectedItem): IPositionVariantItem => {
                    if (selectedItem.type === 'position') {
                        return {
                            position: selectedItem.data,
                            variant: null,
                        };
                    } else {
                        return {
                            position: selectedItem.data.main!!,
                            variant: selectedItem.data,
                        };
                    }
                }));
            }
        });
    }

    // noinspection JSUnusedLocalSymbols
    protected mounted() {
        this.reloadPositions();
        this.reloadVariants();
    }
    // endregion


    // region Утилиты
    private getPositionFullText(positionItem: IPositionItem): string {
        let positionName: string;
        if (positionItem.type === 'position') {
            const position = positionItem.data;

            if (this.$i18n.locale.trim().toLowerCase() === 'kk') {
                positionName = position.nameKk;
            } else {
                positionName = position.nameRu;
            }

            if ((position.categoryNumber === null) || (position.categoryNumber.length === 0)) {
                return positionName;
            }
            return `${position.category?.code}-${position.categoryNumber} - ${positionName}`;
        } else {
            const position = positionItem.data;

            if (this.$i18n.locale.trim().toLowerCase() === 'kk') {
                positionName = position.nameKk;
            } else {
                positionName = position.nameRu;
            }

            if ((position.categoryNumber === null) || (position.categoryNumber.length === 0)) {
                return positionName;
            }
            return `${position.category?.code}-${position.categoryNumber} - ${positionName}`;
        }
    }

    private getPositionText(position: IPositionItem): string {
        const result = this.getPositionFullText(position);
        if (result.length > 50) {
            return result.substr(0, 47) + '...';
        }
        return result;
    }

    private getTranslate(key: string, values?: Values): string {
        return String(this.$t(`modules.budget.staffing_table.positions.*PositionSelection*.${key}`, values));
    }

    private getFieldTranslate(i18nKey: string): string {
        return this.getTranslate(`table_fields.${i18nKey}`)
    };

    private getEnumTranslate(enumName: string, value: string): string {
        return String(this.$t(`modules.budget.staffing_table._enum.${enumName}.${value}`));
    }

    private getCategoryText(category: Dict.PositionCategory | null): string | null {
        if (category === null) {
            return null;
        }
        return `${this.getEnumTranslate('position_category_type', 'EMPTY')} ${category.code}`;
    }

    // Доступ к редактированию данных
    get isEditable(): boolean {
        return staffTabAccess.isEditable;
    }

    get minDate(): Date | null {
        if (this.version === null) {
            return null;
        } else {
            const year = this.version.year;
            return new Date(year, 0, 1);
        }
    }

    get maxDate(): Date | null {
        if (this.version === null) {
            return null;
        } else {
            const year = this.version.year;
            const result = new Date(year + 1, 0, 1);
            result.setMilliseconds(-1);
            return result;
        }
    }

    private formatDate(date: Date | number | null): string {
        if ((date === null) || (date === undefined)) {
            return '';
        }
        return this.$d(date, 'short');
    }

    private getPositionItemId = getPositionItemId;
    // endregion


    // region Основное
    private selectedItems: Array<IPositionItem> = [];

    private copySelectedItemsFromValue() {
        this.selectedItems = this.value.map((value): IPositionItem => {
            const variant = value.variant;
            if (variant === null) {
                return {
                    type: 'position',
                    data: value.position,
                    children: [],
                };
            } else {
                return {
                    type: 'variant',
                    data: variant,
                    children: [],
                };
            }
        });
    }
    // endregion


    // region Данные поиска
    private searchText = '';
    private searchCategory = '';
    private searchFuncBlock = '';
    private searchPosLevel : number | null = null;

    private optionsFuncBlock = [
        { text: 'Не определен', value: '' },
        { text: 'A', value: 'A' },
        { text: 'B', value: 'B' },
        { text: 'C', value: 'C' },
        { text: 'SCS', value: 'SCS' },
    ];
    // endregion


    // region Навигация по страницам
    private itemsPerPageVariants: Array<Comp.DropdownItemDef<number>> = new Array(...itemsPerPageVariants);

    private itemsPerPage = 25;

    private page = 0;

    private get itemCount(): number {
        return this.itemsSorted.length;
    }
    // endregion


    // region Строки
    private get fields(): Comp.TableFieldDef[] {
        const createFieldDef = (dataKey: (keyof Dict.Position) | (keyof Dict.PositionVariants), i18nKey: string): Comp.TableFieldDef => {
            return {
                key: `data.data.${dataKey}`,
                label: this.getTranslate(`table_fields.${i18nKey}`)
            };
        };

        return [
            // Кнопка выбора строки
            {
                key: 'selected',
                label: ''
            },

            // ID
            createFieldDef('id', 'id'),

            // Вид должности
            createFieldDef('kind', 'kind'),

            // Категория должности
            createFieldDef('category', 'category'),

            // № п/п в категории
            createFieldDef('categoryNumber', 'category_number'),

            // Название на казахском
            createFieldDef('nameKk', 'name_kk'),

            // Название на русском
            createFieldDef('nameRu', 'name_ru'),

            // Функциональный блок
            createFieldDef('funcBlock', 'func_block'),

            // Уровень должности
            createFieldDef('posLevel', 'pos_level'),

            // Дата начала действия
            // http://192.168.0.146/issues/7963 - надо скрыть поле
            // createFieldDef('begDate', 'beg_date'),

            // Дата окончания действия
            // http://192.168.0.146/issues/7963 - надо скрыть поле
            // createFieldDef('endDate', 'end_date'),

            // Базовый год отчета, начальное значение
            // http://192.168.0.146/issues/7963 - надо скрыть поле
            // createFieldDef('curYearBeg', 'cur_year_beg'),

            // Базовый год отчета, конечное значение
            // http://192.168.0.146/issues/7963 - надо скрыть поле
            // createFieldDef('curYearEnd', 'cur_year_end'),
        ];
    }

    private sorter: IPositionItemSorter = defaultItemSorter;

    private loadingPositions = false;
    private positions: Array<Dict.Position> = [];
    private loadingVariants = false;
    private variants: Array<Dict.PositionVariants> = [];

    private get loading(): boolean {
        return (this.loadingPositions || this.loadingVariants);
    }

    private get items(): Array<IPositionItem> {
        const result: Array<IPositionItem> = [];
        const passedPositionIdSet = new Set<number>();
        const passedVariantIdSet = new Set<number>();
        const variantToPositionMap = new Map<number, Array<Dict.PositionVariants>>();
        const positionMapById = new Map<number, Dict.Position>();

        // Добавление актуальных вариантов
        this.variants.forEach((variant) => {
            const position = variant.main!!;
            const positionId = position.id!!;
            if (!variantToPositionMap.has(positionId)) {
                variantToPositionMap.set(positionId, []);
            }
            variantToPositionMap.get(positionId)!.push(variant);

            // Если дата начала варианта меньше максимальной даты, то не использовать вариант
            const begDate = variant.begDate;
            const maxDate = this.maxDate;
            if ((begDate !== null) && (maxDate !== null) && (begDate.getTime() > maxDate.getTime())) {
                return;
            }

            // Если дата начала варианта меньше максимальной даты, то не использовать вариант
            const endDate = variant.endDate;
            const minDate = this.minDate;
            if ((endDate !== null) && (minDate !== null) && (endDate.getTime() < minDate.getTime())) {
                return;
            }

            const item: IPositionVariants = {
                type: 'variant',
                data: variant,
                children: [],
            };
            result.push(item);
            passedPositionIdSet.add(positionId);
            passedVariantIdSet.add(variant.id!!);
        });

        // Добавление тех должностей, для которых нет актуальных вариантов
        this.positions.forEach((position) => {
            const id = position.id!!;
            positionMapById.set(id, position);

            // Если вариант для должности уже был добавлен, то должность добавлять не надо
            if (passedPositionIdSet.has(id)) {
                return;
            }

            const item: IPosition = {
                type: 'position',
                data: position,
                children: [],
            };
            result.push(item);
        });

        // Добавление дополнительных элементов в основные элементы
        result.forEach((item) => {
            // Добавить должность, если основной элемент является вариантом
            if (item.type === 'variant') {
                item.children.push({
                    type: 'position',
                    data: item.data.main!!,
                    children: [],
                });
            }

            // Добавить все варианты, относящиеся к должности
            let positionId: number;
            if (item.type === 'position') {
                positionId = item.data.id!!;
            } else {
                positionId = item.data.main!!.id!!;
            }

            variantToPositionMap.get(positionId)?.forEach((variant) => {
                if (!passedVariantIdSet.has(variant.id!!)) {
                    item.children.push({
                        type: 'variant',
                        data: variant,
                        children: [],
                    });
                }
            });
        });

        return result;
    }

    private get itemsFiltered(): Array<IPositionItem> {
        let result: Array<IPositionItem> = new Array(...this.items);

        const searchNameRu = (this.searchText ?? '').trim().toLowerCase();
        const searchCategory = (this.searchCategory ?? '').trim().toLowerCase();
        const searchFuncBlock = (this.searchFuncBlock ?? '').trim().toLowerCase();
        const searchPosLevel = this.searchPosLevel;
        const allowedPosLevels = new Set(this.allowedPosLevels);

        result = result.filter((item) => {
            if ((searchNameRu.length > 0) && (!(item.data.nameRu ?? '').trim().toLowerCase().includes(searchNameRu))) {
                return false;
            }
            if ((searchCategory.length > 0) && (!(item.data.category?.code ?? '').trim().toLowerCase().includes(searchCategory))) {
                return false;
            }
            if ((searchFuncBlock.length > 0) && (!(item.data.funcBlock ?? '').trim().toLowerCase().includes(searchFuncBlock))) {
                return false;
            }
            if ((searchPosLevel !== null) && (item.data.posLevel !== searchPosLevel)) {
                return false;
            }
            if ((allowedPosLevels.size > 0) && (!allowedPosLevels.has(item.data.posLevel))) {
                return false;
            }
            // noinspection RedundantIfStatementJS
            if ((this.version !== null) && (item.type === 'variant') && ((item.data.closingYear ?? Number.MAX_SAFE_INTEGER) <= this.version.year)) {
                return false;
            }
            return true;
        });

        return result;
    }

    private get itemsSorted(): Array<IPositionItem> {
        const result: Array<IPositionItem> = new Array(...this.itemsFiltered);
        result.sort(this.sorter);
        return result;
    }

    private get itemsForPage(): Array<IPositionItem> {
        const result: Array<IPositionItem> = new Array(...this.itemsSorted);
        const start = this.page * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return result.slice(start, end);
    }

    // noinspection JSMismatchedCollectionQueryUpdate
    private get rows(): Array<IRow> {
        const result: Array<IRow> = [];
        const positionIdSet = new Set<number>();
        const variantIdSet = new Set<number>();

        // Заполнение наборов ID выбранных должностей и вариантов должностей
        this.value.forEach((valueItem) => {
            positionIdSet.add(valueItem.position.id!!);

            const variant = valueItem.variant;
            if (variant !== null) {
                variantIdSet.add(variant.id!!);
            }
        })

        this.itemsForPage.forEach((item) => {
            let selected = false;
            if (item.type === 'position') {
                selected = positionIdSet.has(item.data.id!!);
            } else {
                selected = variantIdSet.has(item.data.id!!) || positionIdSet.has(item.data.main!!.id!!);
            }

            const row: IRow = {
                selected,
                data: item,
                _showDetails: (item.children.length > 0),
            };
            result.push(row);
        });
        return result;
    }

    private reloadPositions() {
        if (this.loadingPositions) {
            console.warn('Cannot reload positions - another loading is running');
            return;
        }

        this.request<Array<Dict.Position>>(
            {
                url: '/api/budget/staffing_table/positions',
            },
            (data) => {
                this.positions = data;
            },
            () => 'Error while loading positions',
            () => {
                this.loadingPositions = false;
            }
        );
    }

    private reloadVariants() {
        if (this.loadingVariants) {
            console.warn('Cannot reload position variants - another loading is running');
            return;
        }

        this.request<Array<Dict.PositionVariants>>(
            {
                url: '/api/budget/staffing_table/position-variants',
            },
            (data) => {
                data.forEach((variant) => {
                    // noinspection SuspiciousTypeOfGuard
                    if (typeof variant.begDate === 'number') {
                        variant.begDate = new Date(variant.begDate);
                    }
                    // noinspection SuspiciousTypeOfGuard
                    if (typeof variant.endDate === 'number') {
                        variant.endDate = new Date(variant.endDate);
                    }
                });

                this.variants = data;
            },
            () => 'Error while loading position variants',
            () => {
                this.loadingVariants = false;
            }
        );
    }

    private toggleSelection(position: IPositionItem) {
        if (this.isEditable) {
            let index: number | undefined;
            for (let i = 0; i < this.selectedItems.length; i++) {
                const selectedItem = this.selectedItems[i];
                const selectedId = getPositionItemId(selectedItem);
                const positionId = getPositionItemId(position);
                if (selectedId === positionId) {
                    index = i;
                    break;
                }
            }

            if (index !== undefined) {
                this.selectedItems.splice(index, 1);
            } else {
                if (this.multiple) {
                    this.selectedItems.push(position);
                } else {
                    this.selectedItems = [position];
                }
            }

            this.selectedItems = new Array(...this.selectedItems);
        }
    }

    private onRowSelectionChanged(row: IRow) {
        setTimeout(() => {
            this.toggleSelection(row.data);
        });
    }

    private onRowClicked(row: IRow) {
        if (this.selectOnRowClick) {
            this.toggleSelection(row.data);
        }
    }
    // endregion
}
